import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  ArrowLeft, ArrowRight, Check, Plus, X, Camera,
  User, BookOpen, Award, Share2, Loader2, Search, GraduationCap, Sparkles
} from "lucide-react";
import AIProfileBuilder from "@/components/AIProfileBuilder";
import { ProfileEditHints } from "@/components/OnboardingHints";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WIZARD_STEPS = [
  { id: 1, title: "Basics", icon: User, description: "Name, expertise" },
  { id: 2, title: "About", icon: BookOpen, description: "Bio & languages" },
  { id: 3, title: "Offerings", icon: GraduationCap, description: "Classes & services" },
  { id: 4, title: "Awards", icon: Award, description: "Achievements" },
  { id: 5, title: "Review", icon: Check, description: "Final check" },
];

// Reusable SearchableSelect component
function SearchableSelect({ label, options, value, onChange, placeholder, allowAdd = false, taxonomyType, required = false }) {
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const wrapperRef = useRef(null);

  const filteredOptions = options.filter(opt => opt.toLowerCase().includes(search.toLowerCase()));

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) setIsOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleAddNew = async () => {
    if (!search.trim()) return;
    setAdding(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/taxonomy/add`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ type: taxonomyType, value: search.trim() })
      });
      const data = await response.json();
      if (response.ok) {
        onChange(search.trim());
        toast.success(`Added "${search.trim()}"`);
        setIsOpen(false);
        setSearch("");
      } else {
        toast.error(data.detail || "Failed to add");
      }
    } catch (error) {
      toast.error("Failed to add");
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="space-y-1.5" ref={wrapperRef}>
      <Label>{label} {required && <span className="text-red-500">*</span>}</Label>
      <div className="relative">
        <div className="flex items-center border rounded-lg px-3 py-2.5 cursor-pointer bg-white hover:border-orange-300" onClick={() => setIsOpen(!isOpen)}>
          <Search className="w-4 h-4 text-gray-400 mr-2" />
          <input
            type="text"
            value={isOpen ? search : value}
            onChange={(e) => { setSearch(e.target.value); if (!isOpen) setIsOpen(true); }}
            placeholder={placeholder}
            className="flex-1 outline-none bg-transparent text-sm"
            onFocus={() => setIsOpen(true)}
          />
          {value && !isOpen && <X className="w-4 h-4 text-gray-400 cursor-pointer" onClick={(e) => { e.stopPropagation(); onChange(""); }} />}
        </div>
        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {filteredOptions.length > 0 ? filteredOptions.map((opt, i) => (
              <div key={i} className={`px-3 py-2 cursor-pointer hover:bg-orange-50 text-sm ${opt === value ? "bg-orange-100" : ""}`}
                onClick={() => { onChange(opt); setIsOpen(false); setSearch(""); }}>
                {opt}
              </div>
            )) : <div className="px-3 py-2 text-sm text-gray-500">No matches</div>}
            {allowAdd && search.trim() && !options.includes(search.trim()) && (
              <div className="px-3 py-2 cursor-pointer hover:bg-green-50 text-sm text-green-600 border-t flex items-center gap-2" onClick={handleAddNew}>
                {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add "{search.trim()}"
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function TrainerWizard() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [taxonomy, setTaxonomy] = useState({ trainer_categories: [], cities: [], skills: [] });
  const [uploading, setUploading] = useState(false);
  const [showAIBuilder, setShowAIBuilder] = useState(false);
  const photoInputRef = useRef(null);

  const [formData, setFormData] = useState({
    full_name: "", display_name: "", headline: "", city: "",
    expertise_category: "", specializations: [],
    bio: "", languages: [], teaching_experience: "",
    profile_photo: "",
    offerings: [], // { title, description, duration, price, mode }
    awards: [], certifications: [],
    social_links: [], website: "",
    contact_email: "", contact_phone: "",  // Contact info
    terms_accepted: false
  });

  const [newOffering, setNewOffering] = useState({ title: "", description: "", duration: "", price: "", mode: "online" });

  useEffect(() => {
    fetchTaxonomy();
    fetchExistingProfile();
  }, []);

  const fetchTaxonomy = async () => {
    try {
      const response = await fetch(`${API_URL}/api/taxonomy`);
      if (response.ok) {
        const data = await response.json();
        const grouped = data.grouped || {};
        setTaxonomy({
          trainer_categories: (grouped.trainer_category || []).map(c => c.value),
          cities: (grouped.city || []).map(c => c.value),
          skills: (grouped.skill || []).map(s => s.value)
        });
      }
    } catch (error) {
      console.error("Error fetching taxonomy:", error);
    }
  };

  const fetchExistingProfile = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/api/profiles/me`, { headers: { Authorization: `Bearer ${token}` } });
      if (response.ok) {
        const data = await response.json();
        if (data?.profile_id) {
          // Convert social_presence individual fields to array format for form
          const socialLinks = [];
          const sp = data.social_presence || {};
          if (sp.instagram) socialLinks.push({ platform: "Instagram", url: sp.instagram });
          if (sp.facebook) socialLinks.push({ platform: "Facebook", url: sp.facebook });
          if (sp.facebook_page) socialLinks.push({ platform: "Facebook Page", url: sp.facebook_page });
          if (sp.youtube) socialLinks.push({ platform: "YouTube", url: sp.youtube });
          if (sp.linkedin) socialLinks.push({ platform: "LinkedIn", url: sp.linkedin });
          if (sp.twitter) socialLinks.push({ platform: "Twitter/X", url: sp.twitter });
          if (sp.personal_website) socialLinks.push({ platform: "Website", url: sp.personal_website });
          
          setFormData(prev => ({
            ...prev,
            full_name: data.personal_info?.full_name || "",
            display_name: data.personal_info?.display_name || "",
            headline: data.professional_info?.headline || "",
            city: data.personal_info?.city || "",
            expertise_category: data.professional_info?.primary_category || "",
            specializations: data.professional_info?.skills || [],
            bio: data.professional_info?.bio || "",
            languages: data.personal_info?.languages || [],
            teaching_experience: data.professional_info?.years_of_experience || "",
            profile_photo: data.portfolio?.profile_photo || "",
            offerings: data.trainer_info?.offerings || [],
            awards: data.professional_info?.awards || [],
            certifications: data.professional_info?.trainings || [],
            social_links: socialLinks,
            contact_email: data.contact_management?.contact_email || "",
            contact_phone: data.contact_management?.contact_phone || "",
            terms_accepted: data.preferences?.terms_accepted || false
          }));
        }
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const updateField = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const saveDraft = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const payload = {
        personal_info: {
          full_name: formData.full_name,
          display_name: formData.display_name || formData.full_name.split(" ")[0],
          city: formData.city,
          languages: formData.languages
        },
        professional_info: {
          headline: formData.headline,
          primary_category: formData.expertise_category,
          skills: formData.specializations,
          bio: formData.bio,
          years_of_experience: formData.teaching_experience ? parseInt(formData.teaching_experience) : null,
          awards: formData.awards,
          trainings: formData.certifications
        },
        portfolio: { profile_photo: formData.profile_photo },
        trainer_info: { offerings: formData.offerings },
        social_presence: {
          instagram: formData.social_links.find(l => l.platform === "Instagram")?.url || null,
          facebook: formData.social_links.find(l => l.platform === "Facebook")?.url || null,
          facebook_page: formData.social_links.find(l => l.platform === "Facebook Page")?.url || null,
          youtube: formData.social_links.find(l => l.platform === "YouTube")?.url || null,
          linkedin: formData.social_links.find(l => l.platform === "LinkedIn")?.url || null,
          twitter: formData.social_links.find(l => l.platform === "Twitter/X")?.url || null,
          personal_website: formData.social_links.find(l => l.platform === "Website")?.url || null
        },
        contact_management: {
          contact_email: formData.contact_email,
          contact_phone: formData.contact_phone
        },
        preferences: { terms_accepted: formData.terms_accepted },
        is_draft: true
      };
      const response = await fetch(`${API_URL}/api/profiles/me`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      if (response.ok) toast.success("Draft saved");
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    await saveDraft();
    if (currentStep < WIZARD_STEPS.length) setCurrentStep(currentStep + 1);
  };

  const handleBack = () => { if (currentStep > 1) setCurrentStep(currentStep - 1); };

  // Handle AI Profile Builder output
  const handleAIApply = (aiContent) => {
    setFormData(prev => ({
      ...prev,
      bio: aiContent.bio || prev.bio,
      headline: aiContent.headline || prev.headline,
      specializations: aiContent.skills && aiContent.skills.length > 0 
        ? [...new Set([...prev.specializations, ...aiContent.skills])]
        : prev.specializations,
      expertise_category: aiContent.category || prev.expertise_category
    }));
    if (aiContent.bio) {
      toast.info("AI content applied! Review your bio in the About step.");
    }
  };

  const handleSubmit = async () => {
    if (!formData.full_name || !formData.expertise_category) {
      toast.error("Please fill Name and Expertise Category");
      return;
    }
    if (!formData.terms_accepted) {
      toast.error("Please accept Terms & Conditions");
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const payload = {
        personal_info: {
          full_name: formData.full_name,
          display_name: formData.display_name || formData.full_name.split(" ")[0],
          city: formData.city,
          languages: formData.languages
        },
        professional_info: {
          headline: formData.headline,
          primary_category: formData.expertise_category,
          skills: formData.specializations,
          bio: formData.bio,
          years_of_experience: formData.teaching_experience ? parseInt(formData.teaching_experience) : null,
          awards: formData.awards,
          trainings: formData.certifications
        },
        portfolio: { profile_photo: formData.profile_photo },
        trainer_info: { offerings: formData.offerings },
        social_presence: {
          instagram: formData.social_links.find(l => l.platform === "Instagram")?.url || null,
          facebook: formData.social_links.find(l => l.platform === "Facebook")?.url || null,
          facebook_page: formData.social_links.find(l => l.platform === "Facebook Page")?.url || null,
          youtube: formData.social_links.find(l => l.platform === "YouTube")?.url || null,
          linkedin: formData.social_links.find(l => l.platform === "LinkedIn")?.url || null,
          twitter: formData.social_links.find(l => l.platform === "Twitter/X")?.url || null,
          personal_website: formData.social_links.find(l => l.platform === "Website")?.url || null
        },
        contact_management: {
          contact_email: formData.contact_email,
          contact_phone: formData.contact_phone
        },
        preferences: { terms_accepted: formData.terms_accepted },
        is_draft: false
      };
      const response = await fetch(`${API_URL}/api/profiles/me`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      if (response.ok) {
        toast.success("Profile published!");
        navigate("/talent/dashboard");
      } else {
        const err = await response.json();
        throw new Error(err.detail || "Failed");
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const token = localStorage.getItem("token");
      const fd = new FormData();
      fd.append("file", file);
      fd.append("file_type", "image");
      const response = await fetch(`${API_URL}/api/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd
      });
      if (response.ok) {
        const data = await response.json();
        updateField("profile_photo", data.url);
        toast.success("Uploaded");
      }
    } catch (error) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const addOffering = () => {
    if (!newOffering.title) return;
    updateField("offerings", [...formData.offerings, { ...newOffering }]);
    setNewOffering({ title: "", description: "", duration: "", price: "", mode: "online" });
  };

  const progress = (currentStep / WIZARD_STEPS.length) * 100;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <Button variant="ghost" onClick={() => navigate(-1)}><ArrowLeft className="w-4 h-4 mr-2" /> Back</Button>
            <h1 className="text-lg font-semibold text-orange-600">Create Trainer Profile</h1>
            <Button variant="outline" onClick={saveDraft} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Draft"}
            </Button>
          </div>
          <Progress value={progress} className="h-2 mb-4" />
          <div className="flex justify-between">
            {WIZARD_STEPS.map((step) => {
              const Icon = step.icon;
              const isActive = step.id === currentStep;
              const isCompleted = step.id < currentStep;
              return (
                <div key={step.id} className={`flex flex-col items-center cursor-pointer ${isActive ? "text-orange-600" : isCompleted ? "text-green-600" : "text-gray-400"}`}
                  onClick={() => setCurrentStep(step.id)}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-1 ${isActive ? "bg-orange-100" : isCompleted ? "bg-green-100" : "bg-gray-100"}`}>
                    {isCompleted ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                  </div>
                  <span className="text-xs font-medium hidden sm:block">{step.title}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <Card>
          <CardContent className="p-6">
            {/* Step 1: Basics */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold">Basic Information</h2>
                  <Button 
                    variant="outline"
                    onClick={() => setShowAIBuilder(true)}
                    className="border-orange-300 text-orange-700 hover:bg-orange-50"
                    data-testid="build-with-ai-btn"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Build with AI
                  </Button>
                </div>
                
                <ProfileEditHints 
                  onAIClick={() => setShowAIBuilder(true)}
                  onPhotoClick={() => setCurrentStep(2)}
                  onPublishClick={() => setCurrentStep(5)}
                />
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label>Full Name <span className="text-red-500">*</span></Label>
                    <Input value={formData.full_name} onChange={(e) => updateField("full_name", e.target.value)} placeholder="Your full name" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Display Name</Label>
                    <Input value={formData.display_name} onChange={(e) => updateField("display_name", e.target.value)} placeholder="How students know you" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Headline</Label>
                  <Input value={formData.headline} onChange={(e) => updateField("headline", e.target.value)} placeholder="e.g., Professional Acting Coach | 10+ years experience" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SearchableSelect label="City" options={taxonomy.cities} value={formData.city} onChange={(v) => updateField("city", v)} placeholder="Select city" allowAdd taxonomyType="city" />
                  <SearchableSelect label="Expertise Category" options={taxonomy.trainer_categories} value={formData.expertise_category} onChange={(v) => updateField("expertise_category", v)} placeholder="Select expertise" allowAdd taxonomyType="trainer_category" required />
                </div>
                <div className="space-y-1.5">
                  <Label>Teaching Experience (years)</Label>
                  <Input type="number" value={formData.teaching_experience} onChange={(e) => updateField("teaching_experience", e.target.value)} placeholder="e.g., 8" className="w-32" />
                </div>
                {/* Contact Information */}
                <div className="border-t pt-6 mt-6">
                  <h3 className="text-lg font-medium mb-4">Contact Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label>Contact Email <span className="text-red-500">*</span></Label>
                      <Input type="email" value={formData.contact_email} onChange={(e) => updateField("contact_email", e.target.value)} placeholder="your@email.com" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Contact Phone (optional)</Label>
                      <Input value={formData.contact_phone} onChange={(e) => updateField("contact_phone", e.target.value)} placeholder="+91 98765 43210" />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: About */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <h2 className="text-xl font-semibold">About You</h2>
                <div className="space-y-1.5">
                  <Label>Bio</Label>
                  <Textarea value={formData.bio} onChange={(e) => updateField("bio", e.target.value)} placeholder="Tell students about your teaching philosophy, methods, and what they'll learn..." rows={5} />
                </div>
                <div className="space-y-1.5">
                  <Label>Languages You Teach In</Label>
                  <div className="flex flex-wrap gap-2">
                    {["Hindi", "English", "Tamil", "Telugu", "Kannada", "Malayalam", "Marathi", "Bengali"].map(lang => (
                      <Badge key={lang} variant={formData.languages.includes(lang) ? "default" : "outline"} className="cursor-pointer"
                        onClick={() => updateField("languages", formData.languages.includes(lang) ? formData.languages.filter(l => l !== lang) : [...formData.languages, lang])}>
                        {lang}
                      </Badge>
                    ))}
                  </div>
                </div>
                {/* Profile Photo */}
                <div className="space-y-2">
                  <Label>Profile Photo</Label>
                  <div className="flex items-center gap-4">
                    {formData.profile_photo ? (
                      <div className="relative">
                        <img src={formData.profile_photo} alt="Profile" className="w-24 h-24 object-cover rounded-full" />
                        <button onClick={() => updateField("profile_photo", "")} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1"><X className="w-3 h-3" /></button>
                      </div>
                    ) : (
                      <div className="w-24 h-24 border-2 border-dashed rounded-full flex items-center justify-center cursor-pointer hover:border-orange-400" onClick={() => photoInputRef.current?.click()}>
                        {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <Camera className="w-6 h-6 text-gray-400" />}
                      </div>
                    )}
                    <input ref={photoInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleFileUpload(e.target.files[0])} />
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Offerings */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <h2 className="text-xl font-semibold">Your Offerings</h2>
                <p className="text-gray-500">Add classes, workshops, or coaching services you offer</p>
                
                {/* Add New Offering */}
                <div className="border rounded-lg p-4 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Input placeholder="Class/Service Title" value={newOffering.title} onChange={(e) => setNewOffering({...newOffering, title: e.target.value})} />
                    <select value={newOffering.mode} onChange={(e) => setNewOffering({...newOffering, mode: e.target.value})} className="border rounded-lg px-3 py-2 text-sm">
                      <option value="online">Online</option>
                      <option value="offline">In-Person</option>
                      <option value="both">Both</option>
                    </select>
                  </div>
                  <Textarea placeholder="Description" value={newOffering.description} onChange={(e) => setNewOffering({...newOffering, description: e.target.value})} rows={2} />
                  <div className="grid grid-cols-2 gap-3">
                    <Input placeholder="Duration (e.g., 1 hour)" value={newOffering.duration} onChange={(e) => setNewOffering({...newOffering, duration: e.target.value})} />
                    <Input placeholder="Price (e.g., ₹500/session)" value={newOffering.price} onChange={(e) => setNewOffering({...newOffering, price: e.target.value})} />
                  </div>
                  <Button onClick={addOffering} disabled={!newOffering.title}><Plus className="w-4 h-4 mr-2" /> Add Offering</Button>
                </div>

                {/* Existing Offerings */}
                {formData.offerings.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="font-medium">Your Classes/Services</h3>
                    {formData.offerings.map((off, i) => (
                      <div key={i} className="flex items-start justify-between p-3 bg-orange-50 rounded-lg">
                        <div>
                          <p className="font-medium">{off.title}</p>
                          <p className="text-sm text-gray-600">{off.description}</p>
                          <div className="flex gap-3 mt-1 text-xs text-gray-500">
                            <span>{off.duration}</span>
                            <span>{off.price}</span>
                            <Badge variant="outline" className="text-xs">{off.mode}</Badge>
                          </div>
                        </div>
                        <X className="w-4 h-4 cursor-pointer text-gray-400 hover:text-red-500" onClick={() => updateField("offerings", formData.offerings.filter((_, idx) => idx !== i))} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Step 4: Awards */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <h2 className="text-xl font-semibold">Awards & Certifications</h2>
                <div className="space-y-2">
                  <Label>Awards / Recognition</Label>
                  <div className="flex gap-2">
                    <Input placeholder="Add award" onKeyDown={(e) => { if (e.key === "Enter" && e.target.value.trim()) { updateField("awards", [...formData.awards, e.target.value.trim()]); e.target.value = ""; } }} />
                    <Button variant="outline"><Plus className="w-4 h-4" /></Button>
                  </div>
                  {formData.awards.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {formData.awards.map((a, i) => <Badge key={i} variant="secondary" className="gap-1">{a}<X className="w-3 h-3 cursor-pointer" onClick={() => updateField("awards", formData.awards.filter((_, idx) => idx !== i))} /></Badge>)}
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Certifications</Label>
                  <div className="flex gap-2">
                    <Input placeholder="Add certification" onKeyDown={(e) => { if (e.key === "Enter" && e.target.value.trim()) { updateField("certifications", [...formData.certifications, e.target.value.trim()]); e.target.value = ""; } }} />
                    <Button variant="outline"><Plus className="w-4 h-4" /></Button>
                  </div>
                  {formData.certifications.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {formData.certifications.map((c, i) => <Badge key={i} variant="secondary" className="gap-1">{c}<X className="w-3 h-3 cursor-pointer" onClick={() => updateField("certifications", formData.certifications.filter((_, idx) => idx !== i))} /></Badge>)}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 5: Review */}
            {currentStep === 5 && (
              <div className="space-y-6">
                <h2 className="text-xl font-semibold">Review & Publish</h2>
                <div className="space-y-2">
                  <Label>Social Links</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {["Instagram", "Facebook", "Facebook Page", "YouTube", "LinkedIn", "Twitter/X", "Website"].map((platform) => (
                      <div key={platform} className="flex items-center gap-2">
                        <span className="text-sm w-28 text-gray-600">{platform}</span>
                        <Input placeholder={`${platform} URL`}
                          value={formData.social_links.find(l => l.platform === platform)?.url || ""}
                          onChange={(e) => {
                            const existing = formData.social_links.filter(l => l.platform !== platform);
                            if (e.target.value.trim()) updateField("social_links", [...existing, { platform, url: e.target.value.trim() }]);
                            else updateField("social_links", existing);
                          }} className="flex-1" />
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-orange-50 rounded-lg p-4 space-y-2">
                  <h3 className="font-medium">Profile Summary</h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="text-gray-500">Name:</span> {formData.full_name || "-"}</div>
                    <div><span className="text-gray-500">City:</span> {formData.city || "-"}</div>
                    <div><span className="text-gray-500">Expertise:</span> {formData.expertise_category || "-"}</div>
                    <div><span className="text-gray-500">Offerings:</span> {formData.offerings.length} added</div>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 border rounded-lg">
                  <input type="checkbox" checked={formData.terms_accepted} onChange={(e) => updateField("terms_accepted", e.target.checked)} className="mt-1 w-5 h-5" />
                  <div>
                    <Label>I accept the Terms of Service and Privacy Policy *</Label>
                    <p className="text-sm text-gray-500">By checking this, you agree to our terms.</p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between mt-6">
          <Button variant="outline" onClick={handleBack} disabled={currentStep === 1}><ArrowLeft className="w-4 h-4 mr-2" /> Previous</Button>
          {currentStep < WIZARD_STEPS.length ? (
            <Button onClick={handleNext} disabled={saving} className="bg-orange-600 hover:bg-orange-700">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Next <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={loading} className="bg-green-600 hover:bg-green-700">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />} Publish Profile
            </Button>
          )}
        </div>
      </div>

      {/* AI Profile Builder Modal */}
      <AIProfileBuilder
        isOpen={showAIBuilder}
        onClose={() => setShowAIBuilder(false)}
        profileType="trainer"
        onApply={handleAIApply}
        existingData={{
          bio: formData.bio,
          headline: formData.headline,
          skills: formData.specializations
        }}
      />
    </div>
  );
}
